import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {
    PlayArrow as PlayIcon,
    Pause as PauseIcon,
    DeleteOutline as ClearIcon,
} from '@mui/icons-material';

const RATE_WINDOW_MS = 3000;        // window used to compute Hz
const RING_SIZE = 64;               // max timestamps kept per (sys.cmp.name)
const REFRESH_INTERVAL_MS = 500;    // UI refresh cadence

function fieldsToString(fields) {
    if (!fields || typeof fields !== 'object') return String(fields);
    try {
        return JSON.stringify(fields, (key, value) => {
            if (typeof value === 'bigint') return value.toString();
            if (value instanceof Uint8Array || ArrayBuffer.isView(value)) {
                return Array.from(value);
            }
            return value;
        });
    } catch {
        return String(fields);
    }
}

export default function MonitorPage() {
    // Map<key, { sysId, compId, name, count, ts: number[], lastFields }>
    const statsRef = useRef(new Map());
    const pausedRef = useRef(false);
    const [paused, setPaused] = useState(false);
    const [tick, setTick] = useState(0);
    const [filterName, setFilterName] = useState('');
    const [filterSysId, setFilterSysId] = useState('all');
    const [sortBy, setSortBy] = useState('name'); // 'name' | 'hz' | 'count'
    const [expanded, setExpanded] = useState(null);

    useEffect(() => { pausedRef.current = paused; }, [paused]);

    useEffect(() => {
        const handler = (event, name, fields, header) => {
            if (pausedRef.current) return;
            const sysId = header && header.srcSystem !== undefined ? header.srcSystem : '?';
            const compId = header && header.srcComponent !== undefined ? header.srcComponent : '?';
            const key = `${sysId}.${compId}.${name}`;
            const now = Date.now();
            let entry = statsRef.current.get(key);
            if (!entry) {
                entry = { sysId, compId, name, count: 0, ts: [], lastFields: null };
                statsRef.current.set(key, entry);
            }
            entry.count += 1;
            entry.lastFields = fields;
            entry.ts.push(now);
            if (entry.ts.length > RING_SIZE) entry.ts.splice(0, entry.ts.length - RING_SIZE);
        };
        window.api.onMavlinkMessage(handler);
        const id = setInterval(() => setTick((t) => t + 1), REFRESH_INTERVAL_MS);
        return () => {
            window.api.removeOnMavlinkMessage(handler);
            clearInterval(id);
        };
    }, []);

    const handleClear = () => {
        statsRef.current.clear();
        setTick((t) => t + 1);
        setExpanded(null);
    };

    const sysIds = useMemo(() => {
        const s = new Set();
        statsRef.current.forEach((e) => s.add(String(e.sysId)));
        return Array.from(s).sort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tick]);

    const rows = useMemo(() => {
        const now = Date.now();
        const needle = filterName.trim().toLowerCase();
        const out = [];
        statsRef.current.forEach((e, key) => {
            if (filterSysId !== 'all' && String(e.sysId) !== filterSysId) return;
            if (needle && !e.name.toLowerCase().includes(needle)) return;
            const recent = e.ts.filter((t) => now - t <= RATE_WINDOW_MS);
            let hz = 0;
            if (recent.length >= 2) {
                const span = (recent[recent.length - 1] - recent[0]) / 1000;
                hz = span > 0 ? (recent.length - 1) / span : 0;
            }
            const stale = e.ts.length > 0 && now - e.ts[e.ts.length - 1] > RATE_WINDOW_MS;
            out.push({ key, ...e, hz, stale });
        });
        if (sortBy === 'hz') out.sort((a, b) => b.hz - a.hz);
        else if (sortBy === 'count') out.sort((a, b) => b.count - a.count);
        else out.sort((a, b) =>
            a.sysId !== b.sysId ? String(a.sysId).localeCompare(String(b.sysId))
            : a.compId !== b.compId ? String(a.compId).localeCompare(String(b.compId))
            : a.name.localeCompare(b.name)
        );
        return out;
    }, [tick, filterName, filterSysId, sortBy]);

    const totalCount = useMemo(() => {
        let n = 0;
        statsRef.current.forEach((e) => { n += e.count; });
        return n;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tick]);

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, p: '2px', gap: '2px', boxSizing: 'border-box' }}>
            <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="h6" sx={{ mr: 2 }}>MAVLink Monitor</Typography>
                    <Button
                        size="small"
                        variant={paused ? 'contained' : 'outlined'}
                        startIcon={paused ? <PlayIcon /> : <PauseIcon />}
                        color={paused ? 'warning' : 'primary'}
                        onClick={() => setPaused((p) => !p)}
                    >
                        {paused ? 'Resume' : 'Pause'}
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ClearIcon />}
                        onClick={handleClear}
                    >
                        Clear
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        {rows.length} messages · received {totalCount}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
                    <TextField
                        size="small"
                        label="Filter by message name"
                        placeholder="e.g. ATTITUDE"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        sx={{ flexGrow: 1, maxWidth: 320 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="monitor-sysid-label">System ID</InputLabel>
                        <Select
                            labelId="monitor-sysid-label"
                            label="System ID"
                            value={filterSysId}
                            onChange={(e) => setFilterSysId(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            {sysIds.map((id) => (
                                <MenuItem key={id} value={id}>{id}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel id="monitor-sort-label">Sort by</InputLabel>
                        <Select
                            labelId="monitor-sort-label"
                            label="Sort by"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <MenuItem value="name">Name</MenuItem>
                            <MenuItem value="hz">Rate (Hz)</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    fontSize: 12,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '80px 1fr 100px',
                        position: 'sticky',
                        top: 0,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        py: 0.5,
                        px: 1,
                        fontWeight: 600,
                        zIndex: 1,
                    }}
                >
                    <Box>Sys.Cmp</Box>
                    <Box>Message</Box>
                    <Box sx={{ textAlign: 'right', pr: 1 }}>Hz</Box>
                </Box>
                {rows.length === 0 ? (
                    <Box sx={{ p: 2, color: 'text.secondary' }}>
                        {statsRef.current.size === 0
                            ? 'Waiting for MAVLink traffic…'
                            : 'No messages match the current filters.'}
                    </Box>
                ) : (
                    rows.map((r) => {
                        const isExpanded = expanded === r.key;
                        return (
                            <React.Fragment key={r.key}>
                                <Box
                                    onClick={() => setExpanded(isExpanded ? null : r.key)}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '80px 1fr 100px',
                                        px: 1,
                                        py: 0.25,
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: 'action.hover' },
                                        bgcolor: isExpanded ? 'action.selected' : 'transparent',
                                        opacity: r.stale ? 0.5 : 1,
                                    }}
                                >
                                    <Box>{r.sysId}.{r.compId}</Box>
                                    <Box sx={{ color: 'primary.main' }}>{r.name}</Box>
                                    <Box sx={{ textAlign: 'right', pr: 1 }}>
                                        {r.hz >= 10 ? r.hz.toFixed(0) : r.hz.toFixed(1)}
                                    </Box>
                                </Box>
                                {isExpanded && (
                                    <Box
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                            bgcolor: 'action.hover',
                                        }}
                                    >
                                        {r.lastFields && typeof r.lastFields === 'object' ? (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'max-content 1fr',
                                                    columnGap: 2,
                                                    rowGap: 0.25,
                                                }}
                                            >
                                                {Object.entries(r.lastFields).map(([k, v]) => (
                                                    <React.Fragment key={k}>
                                                        <Box sx={{ color: 'text.secondary' }}>{k}</Box>
                                                        <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                                            {fieldsToString(v)}
                                                        </Box>
                                                    </React.Fragment>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Box sx={{ whiteSpace: 'pre-wrap' }}>
                                                {fieldsToString(r.lastFields)}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </React.Fragment>
                        );
                    })
                )}
            </Box>
        </Box>
    );
}
